/* import __COLOCATED_TEMPLATE__ from './slack.hbs'; */
/* RESPONSIBLE TEAM: team-omni */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { taskFor } from 'ember-concurrency-ts';
import type Store from '@ember-data/store';
import type SlackIntegrationModel from 'embercom/models/slack/integration';
import { get } from 'embercom/lib/ajax';

interface Args {
  model: {
    settings: Record<string, unknown>;
  };
}

export default class Slack extends Component<Args> {
  @service declare appService: any;
  @service declare redirectService: any;
  @service declare notificationsService: any;
  @service declare intl: any;
  @service declare store: Store;

  @tracked slackIntegrations: SlackIntegrationModel[] | null = null;
  @tracked removingIntegration: SlackIntegrationModel | null = null;
  @tracked isRemoveModalOpen = false;
  @tracked fetchError: string | null = null;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    taskFor(this.fetchSlackIntegrations).perform();
  }

  @dropTask
  *fetchSlackIntegrations(): TaskGenerator<void> {
    try {
      let integrations = yield this.store.query('slack/integration', {});
      this.slackIntegrations = integrations.toArray();
    } catch (error) {
      console.error('Failed to fetch Slack integrations:', error);
      this.fetchError = this.intl.t('new-settings.channels.slack.fetch-integrations-error');
    }
  }

  @dropTask
  *authenticate(): TaskGenerator<void> {
    let response = yield get(`/ember/slack/v2/integrations/install`, {
      app_id: this.appService.app.id,
      admin_id: this.appService.app.currentAdmin.id,
    });
    this.redirectService.redirect(response.url);
  }

  @dropTask
  *removeIntegration(): TaskGenerator<void> {
    if (!this.removingIntegration) {
      return;
    }
    try {
      yield this.removingIntegration.destroyRecord();
      this.notificationsService.notifyConfirmation(
        this.intl.t('new-settings.channels.slack.connected-accounts.remove-success'),
      );
      this.closeRemoveModal();
      yield taskFor(this.fetchSlackIntegrations).perform();
    } catch (error) {
      console.error('Failed to remove Slack integration:', error);
      this.notificationsService.notifyError(
        this.intl.t('new-settings.channels.slack.connected-accounts.remove-error'),
      );
    }
  }

  @action
  showRemoveModal(integration: SlackIntegrationModel) {
    this.removingIntegration = integration;
    this.isRemoveModalOpen = true;
  }

  @action
  closeRemoveModal() {
    this.removingIntegration = null;
    this.isRemoveModalOpen = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Channels::Slack': typeof Slack;
    'new-settings/channels/slack': typeof Slack;
  }
}
